import { Link } from "react-router-dom";

/* ============================================================================
 * Loftet — For norske poengjegere. Aviation-inspired, premium, data-driven.
 * Deep navy + champagne + ivory. Fraunces editorial serif + IBM Plex Sans/Mono.
 * Visual language: boarding pass, departure board split-flap, flight paths.
 * ========================================================================== */

const P = {
  bg: "#0A1628",          // deep midnight navy
  panel: "#102338",       // raised navy panel
  ink: "#F5EFE0",         // warm ivory text
  inkDim: "#94A3B8",      // muted slate
  champagne: "#D4A957",   // matte gold
  champagneSoft: "#E5C77F",
  cloud: "#F8F5EE",       // soft ivory for light surfaces
  cloudDark: "#1B2D45",
  divider: "#1F3149",
};
const D = "'Fraunces', 'Georgia', serif";
const S = "'IBM Plex Sans', system-ui, sans-serif";
const M = "'IBM Plex Mono', monospace";

export default function MockupLoftet() {
  return (
    <div style={{ background: P.bg, color: P.ink, fontFamily: S, minHeight: "100vh" }}>
      <TopBar />
      <section style={{ maxWidth: 1180, margin: "0 auto", padding: "0 28px 100px" }}>
        <Section label="01 · LANDING"><LoftetLanding /></Section>
        <Section label="02 · EXTENSION POPUP">
          <div style={{ background: P.cloudDark, padding: "60px 0", display: "flex", justifyContent: "center", position: "relative", overflow: "hidden", border: `1px solid ${P.divider}` }}>
            <FlightArc />
            <LoftetPopup />
          </div>
        </Section>
        <Section label="03 · CONSUMER DASHBOARD"><LoftetDashboard /></Section>
      </section>
    </div>
  );
}

function TopBar() {
  return (
    <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(10,22,40,0.9)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${P.divider}` }}>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "14px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/mockups" style={{ fontFamily: M, fontSize: 10, color: P.inkDim, textDecoration: "none", letterSpacing: "0.2em" }}>← ALLE RETNINGER</Link>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Wing />
          <span style={{ fontFamily: D, fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em" }}>Loftet</span>
        </span>
        <span style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.25em", color: P.champagne }}>RETNING 04 — POENGJEGERE</span>
      </div>
    </div>
  );
}

function Wing() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={P.champagne} strokeWidth="1.5">
      <path d="M2 18 L12 6 L22 18 M7 18 L12 13 L17 18" />
    </svg>
  );
}

function FlightArc() {
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity: 0.18 }} viewBox="0 0 1100 400" preserveAspectRatio="none">
      <path d="M50 350 Q 550 -40 1050 350" stroke={P.champagne} strokeWidth="1.5" strokeDasharray="4 6" fill="none" />
      <circle cx="50" cy="350" r="4" fill={P.champagne} />
      <circle cx="1050" cy="350" r="4" fill={P.champagne} />
    </svg>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ paddingTop: 64 }}>
      <div style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.3em", color: P.champagne, marginBottom: 18 }}>{label}</div>
      {children}
    </div>
  );
}

function LoftetLanding() {
  return (
    <div style={{ background: P.panel, border: `1px solid ${P.divider}`, overflow: "hidden" }}>
      <nav style={{ padding: "22px 48px", borderBottom: `1px solid ${P.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}><Wing /><span style={{ fontFamily: D, fontSize: 26, fontWeight: 500, letterSpacing: "-0.01em" }}>Loftet</span></div>
        <div style={{ display: "flex", gap: 32, fontFamily: M, fontSize: 11, letterSpacing: "0.18em", color: P.inkDim }}>
          <span>METODE</span><span>RUTER</span><span>MEDLEMSKAP</span>
        </div>
        <button style={{ background: "transparent", color: P.ink, border: `1px solid ${P.champagne}`, padding: "9px 18px", fontFamily: M, fontSize: 11, letterSpacing: "0.2em", cursor: "pointer" }}>LOGG INN →</button>
      </nav>

      {/* Departure-board strip */}
      <div style={{ background: "#06101D", padding: "10px 48px", borderBottom: `1px solid ${P.divider}`, display: "flex", gap: 28, fontFamily: M, fontSize: 11, color: P.champagneSoft, letterSpacing: "0.12em", overflow: "hidden", whiteSpace: "nowrap" }}>
        <span>● LIVE</span>
        <span>KOMPLETT.NO ─ SK 4302 ─ +15 P / 100 KR</span>
        <span style={{ color: P.inkDim }}>·</span>
        <span>XXL.NO ─ SK 8801 ─ +8 P / 100 KR</span>
        <span style={{ color: P.inkDim }}>·</span>
        <span>MENY.NO ─ SK 1140 ─ TRUMF 3 %</span>
        <span style={{ color: P.inkDim }}>·</span>
        <span>HOTELLS.COM ─ SK 9120 ─ +10 P / 100 KR</span>
        <span style={{ color: P.inkDim }}>·</span>
        <span>LEGO.COM ─ SK 6604 ─ +5 P / 100 KR</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 0, position: "relative" }}>
        <div style={{ padding: "72px 48px 64px", borderRight: `1px solid ${P.divider}` }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 14px", border: `1px solid ${P.champagne}`, color: P.champagne, fontFamily: M, fontSize: 10, letterSpacing: "0.25em", marginBottom: 32 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: P.champagne }} />
            FOR EUROBONUS-JEGERE
          </div>
          <h1 style={{ fontFamily: D, fontSize: "clamp(56px, 7vw, 100px)", fontWeight: 400, letterSpacing: "-0.03em", lineHeight: 0.94, margin: 0 }}>
            <span style={{ fontStyle: "italic", color: P.champagne }}>Hver krone</span> du bruker<br />kan ta deg <span style={{ fontStyle: "italic" }}>lenger.</span>
          </h1>
          <p style={{ marginTop: 32, fontSize: 17, lineHeight: 1.6, color: P.inkDim, maxWidth: 460 }}>
            Loftet er verktøyet for norske poengjegere. Vi sjekker hver butikk for EuroBonus-poeng, sammenligner det med Trumf og kortbonuser, og forteller deg <em style={{ color: P.ink, fontStyle: "italic" }}>presist</em> hvordan du tjener mest.
          </p>
          <div style={{ marginTop: 40, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <button style={{ background: P.champagne, color: "#0A1628", border: "none", padding: "16px 28px", fontFamily: M, fontWeight: 600, fontSize: 12, letterSpacing: "0.2em", cursor: "pointer" }}>BOARD NOW →</button>
            <span style={{ fontFamily: M, fontSize: 11, color: P.inkDim, letterSpacing: "0.15em" }}>14 DAGER GRATIS · 15 KR/MND</span>
          </div>
          <div style={{ marginTop: 56, display: "flex", gap: 40, paddingTop: 28, borderTop: `1px solid ${P.divider}` }}>
            {[["1 247", "MEDLEMMER OM BORD"], ["94 800", "POENG SPORET I JUNI"], ["38", "BUTIKKER MED EUROBONUS"]].map(([v, l]) => (
              <div key={l}>
                <div style={{ fontFamily: D, fontSize: 36, fontWeight: 500, letterSpacing: "-0.02em", color: P.champagne }}>{v}</div>
                <div style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.2em", color: P.inkDim, marginTop: 4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Boarding pass card */}
        <div style={{ padding: "72px 48px", background: P.bg, position: "relative" }}>
          <FlightArc />
          <div style={{ position: "relative", background: P.cloud, color: "#0A1628", padding: 28, fontFamily: M, fontSize: 11 }}>
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: `1px dashed #C8BFA8`, paddingBottom: 16 }}>
              <div>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>LOFTET BOARDING PASS</div>
                <div style={{ fontFamily: D, fontSize: 26, fontWeight: 500, letterSpacing: "-0.01em", marginTop: 4 }}>Komplett</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>FLIGHT</div>
                <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "0.1em" }}>SK 4302</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 1fr", alignItems: "center", padding: "24px 0", borderBottom: `1px dashed #C8BFA8` }}>
              <div>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>FRA · KASSEN</div>
                <div style={{ fontFamily: D, fontSize: 28, fontWeight: 500 }}>KMP</div>
                <div style={{ fontSize: 10, color: "#8A7C5F" }}>komplett.no</div>
              </div>
              <div style={{ textAlign: "center", color: P.champagne }}>
                <svg width="40" height="20" viewBox="0 0 40 20" fill="currentColor"><path d="M2 10 L34 10 M30 6 L34 10 L30 14" stroke="currentColor" strokeWidth="1.5" fill="none" /><circle cx="36" cy="10" r="2" /></svg>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>TIL · BONUS</div>
                <div style={{ fontFamily: D, fontSize: 28, fontWeight: 500, color: P.champagne }}>EB</div>
                <div style={{ fontSize: 10, color: "#8A7C5F" }}>EuroBonus</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", paddingTop: 16, gap: 12 }}>
              <div>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>EARN RATE</div>
                <div style={{ fontFamily: D, fontSize: 22, fontWeight: 500, color: P.champagne }}>15 p</div>
                <div style={{ fontSize: 9, color: "#8A7C5F" }}>/ 100 kr</div>
              </div>
              <div>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>+ NIVÅ</div>
                <div style={{ fontFamily: D, fontSize: 22, fontWeight: 500 }}>GULL</div>
                <div style={{ fontSize: 9, color: "#8A7C5F" }}>2x multiplier</div>
              </div>
              <div>
                <div style={{ fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>METODER</div>
                <div style={{ fontFamily: D, fontSize: 22, fontWeight: 500 }}>04</div>
                <div style={{ fontSize: 9, color: "#8A7C5F" }}>verifisert</div>
              </div>
            </div>
            {/* perforation */}
            <div style={{ height: 1, borderTop: `2px dashed #C8BFA8`, margin: "20px -28px 16px", position: "relative" }}>
              <span style={{ position: "absolute", left: -12, top: -12, width: 24, height: 24, borderRadius: "50%", background: P.bg }} />
              <span style={{ position: "absolute", right: -12, top: -12, width: 24, height: 24, borderRadius: "50%", background: P.bg }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 9, letterSpacing: "0.2em", color: "#8A7C5F" }}>
              <span>SEAT · 12A</span>
              <span>GATE · OPEN</span>
              <span>VALID · 14.06.26</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3 columns "How it works" — premium editorial */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderTop: `1px solid ${P.divider}` }}>
        {[
          { n: "I", t: "Skann ruten", b: "Loftet leser hver butikk du besøker og finner alle EuroBonus-, Trumf- og kort-multiplikatorer som gjelder." },
          { n: "II", t: "Sammenlign", b: "Hvilket kort gir flest poeng på akkurat denne handelen? Loftet beregner det presist før du betaler." },
          { n: "III", t: "Konverter", b: "Se dine opptjente poeng i sanntid — og hvor mange flyvninger du har tjent inn denne måneden." },
        ].map((f) => (
          <div key={f.n} style={{ padding: "56px 36px", borderRight: `1px solid ${P.divider}`, background: P.bg }}>
            <div style={{ fontFamily: D, fontSize: 56, fontWeight: 400, fontStyle: "italic", color: P.champagne, letterSpacing: "-0.02em", lineHeight: 1 }}>{f.n}.</div>
            <div style={{ fontFamily: D, fontSize: 26, fontWeight: 500, marginTop: 28, letterSpacing: "-0.01em" }}>{f.t}</div>
            <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.65, color: P.inkDim }}>{f.b}</div>
          </div>
        ))}
      </div>

      {/* Membership card */}
      <div style={{ padding: "72px 48px", borderTop: `1px solid ${P.divider}`, background: P.panel }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 56, alignItems: "center" }}>
          <div>
            <div style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.3em", color: P.champagne, marginBottom: 20 }}>MEDLEMSKAP — UTEN BINDING</div>
            <h2 style={{ fontFamily: D, fontSize: 56, fontWeight: 400, lineHeight: 1.05, margin: 0, letterSpacing: "-0.02em" }}>
              For <em style={{ fontStyle: "italic", color: P.champagne }}>femten kroner</em><br />i måneden får du<br />en hel klasse oppgradering.
            </h2>
          </div>
          <div style={{ position: "relative", padding: 32, background: "linear-gradient(135deg, #1B2D45 0%, #0A1628 100%)", border: `1px solid ${P.champagne}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 28 }}>
              <Wing />
              <span style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.2em", color: P.champagne }}>LOFTET MEMBER</span>
            </div>
            <div style={{ fontFamily: D, fontSize: 84, fontWeight: 500, letterSpacing: "-0.04em", lineHeight: 1 }}>15<span style={{ fontFamily: M, fontSize: 16, color: P.inkDim, letterSpacing: "0.15em", marginLeft: 4 }}>KR/MND</span></div>
            <ul style={{ listStyle: "none", padding: 0, margin: "28px 0 0", fontSize: 13, fontFamily: M, color: P.inkDim, lineHeight: 2.1, letterSpacing: "0.05em" }}>
              {["► EuroBonus-tracker · alle butikker","► Kort-kombo kalkulator","► Trumf + Coop som backup","► Månedlig flymilrapport","► Avslutt når du vil"].map((l) => <li key={l}>{l}</li>)}
            </ul>
            <button style={{ width: "100%", marginTop: 28, background: P.champagne, color: "#0A1628", border: "none", padding: 14, fontFamily: M, fontWeight: 600, fontSize: 12, letterSpacing: "0.25em", cursor: "pointer" }}>RESERVER PLASSEN DIN →</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LoftetPopup() {
  return (
    <div style={{ width: 360, background: P.cloud, color: "#0A1628", fontFamily: S, position: "relative", zIndex: 2 }}>
      <div style={{ padding: "14px 20px", borderBottom: `1px solid #D8CFB8`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0A1628" strokeWidth="1.5"><path d="M2 18 L12 6 L22 18 M7 18 L12 13 L17 18" /></svg>
          <span style={{ fontFamily: D, fontSize: 18, fontWeight: 500 }}>Loftet</span>
        </span>
        <span style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.2em", color: "#8A7C5F" }}>↻ SCAN</span>
      </div>

      {/* Departure-board flap row */}
      <div style={{ background: "#0A1628", color: P.champagne, padding: "8px 20px", display: "flex", justifyContent: "space-between", fontFamily: M, fontSize: 10, letterSpacing: "0.18em" }}>
        <span>● LIVE</span><span>SK 4302</span><span>KMP → EB</span><span>GATE OPEN</span>
      </div>

      <div style={{ padding: "18px 20px 0" }}>
        <div style={{ fontFamily: M, fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F" }}>NÅVÆRENDE NETTSTED</div>
        <div style={{ fontFamily: D, fontSize: 24, fontWeight: 500, letterSpacing: "-0.01em", marginTop: 2 }}>komplett.no</div>
        <div style={{ marginTop: 6, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: M, fontSize: 10, letterSpacing: "0.15em", color: "#2F6A4B" }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2F6A4B" }} /> VERIFISERT · 4 METODER
        </div>
      </div>

      {/* Big earn rate */}
      <div style={{ margin: "16px 20px", background: "#0A1628", color: P.ink, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div>
            <div style={{ fontFamily: M, fontSize: 9, letterSpacing: "0.25em", color: P.champagne }}>EUROBONUS</div>
            <div style={{ fontFamily: D, fontSize: 56, fontWeight: 500, lineHeight: 0.95, letterSpacing: "-0.04em", color: P.champagne }}>+15<span style={{ fontSize: 18, color: P.inkDim, marginLeft: 6 }}>P</span></div>
            <div style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.15em", color: P.inkDim, marginTop: 4 }}>PER 100 KR</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontFamily: M, fontSize: 9, letterSpacing: "0.25em", color: P.inkDim }}>MED GULL-NIVÅ</div>
            <div style={{ fontFamily: D, fontSize: 28, fontWeight: 500, color: P.ink, marginTop: 4 }}>×2</div>
            <div style={{ fontFamily: M, fontSize: 9, color: P.inkDim, letterSpacing: "0.15em" }}>= 30 P / 100 KR</div>
          </div>
        </div>
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${P.divider}`, fontFamily: M, fontSize: 10, color: P.inkDim, letterSpacing: "0.1em", display: "flex", justifyContent: "space-between" }}>
          <span>≈ 6 % AVKASTNING</span>
          <span style={{ color: P.champagne }}>BEST PÅ DENNE BUTIKKEN ★</span>
        </div>
      </div>

      <div style={{ padding: "8px 20px" }}>
        <div style={{ fontFamily: M, fontSize: 9, letterSpacing: "0.25em", color: "#8A7C5F", marginBottom: 8 }}>BETALINGSMETODER · 04</div>
        {[
          ["Visa", "OK", true],
          ["Mastercard", "OK", true],
          ["Vipps", "OK", true],
          ["Klarna", "OK", true],
        ].map(([n, s]) => (
          <div key={n} style={{ display: "grid", gridTemplateColumns: "1fr 60px 30px", padding: "8px 0", borderBottom: `1px dashed #D8CFB8`, fontSize: 13, alignItems: "center" }}>
            <span style={{ fontFamily: D, fontWeight: 500 }}>{n}</span>
            <span style={{ fontFamily: M, fontSize: 10, color: "#8A7C5F", letterSpacing: "0.15em" }}>{s}</span>
            <span style={{ color: "#2F6A4B", textAlign: "right", fontFamily: M, fontSize: 11 }}>✓</span>
          </div>
        ))}
      </div>

      <div style={{ padding: "12px 20px 16px", borderTop: `1px solid #D8CFB8`, marginTop: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontFamily: M, fontSize: 10, color: "#8A7C5F", letterSpacing: "0.15em" }}>SEAT 12A · 4/7 FOUND</span>
        <div style={{ display: "flex", fontFamily: M, fontSize: 10, letterSpacing: "0.15em" }}>
          <span style={{ padding: "4px 8px", background: "#0A1628", color: P.champagne }}>NO</span>
          <span style={{ padding: "4px 8px", color: "#8A7C5F" }}>EN</span>
        </div>
      </div>
    </div>
  );
}

function LoftetDashboard() {
  return (
    <div style={{ background: P.panel, border: `1px solid ${P.divider}` }}>
      <nav style={{ padding: "18px 36px", borderBottom: `1px solid ${P.divider}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", gap: 40, alignItems: "center" }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Wing /><span style={{ fontFamily: D, fontSize: 22, fontWeight: 500 }}>Loftet</span></span>
          <div style={{ display: "flex", gap: 28, fontFamily: M, fontSize: 11, letterSpacing: "0.18em" }}>
            <span style={{ color: P.champagne, borderBottom: `1px solid ${P.champagne}`, paddingBottom: 4 }}>OVERSIKT</span>
            <span style={{ color: P.inkDim }}>RUTER</span>
            <span style={{ color: P.inkDim }}>POENG</span>
            <span style={{ color: P.inkDim }}>NIVÅ</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <span style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.2em", color: P.inkDim }}>NO / EN</span>
          <span style={{ padding: "4px 10px", border: `1px solid ${P.champagne}`, color: P.champagne, fontFamily: M, fontSize: 10, letterSpacing: "0.2em" }}>GULL</span>
          <span style={{ width: 34, height: 34, borderRadius: "50%", background: P.champagne, color: "#0A1628", display: "grid", placeItems: "center", fontFamily: D, fontWeight: 600 }}>S</span>
        </div>
      </nav>

      {/* Hero stat */}
      <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", borderBottom: `1px solid ${P.divider}` }}>
        <div style={{ padding: "44px 36px", borderRight: `1px solid ${P.divider}`, position: "relative", overflow: "hidden" }}>
          <FlightArc />
          <div style={{ position: "relative" }}>
            <div style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.3em", color: P.inkDim, marginBottom: 18 }}>OPPTJENT · JUNI 2026</div>
            <div style={{ fontFamily: D, fontSize: 130, fontWeight: 400, letterSpacing: "-0.05em", lineHeight: 0.9, color: P.champagne }}>
              12 480<span style={{ fontFamily: M, fontSize: 16, color: P.inkDim, letterSpacing: "0.15em", marginLeft: 10 }}>POENG</span>
            </div>
            <div style={{ marginTop: 18, display: "flex", gap: 28, fontFamily: M, fontSize: 11, letterSpacing: "0.15em" }}>
              <span style={{ color: "#76C893" }}>↑ 22% VS MAI</span>
              <span style={{ color: P.inkDim }}>≈ 1 248 KR I VERDI</span>
              <span style={{ color: P.inkDim }}>TOP 8% I NORGE</span>
            </div>
            {/* Mini bar chart — 12 months. Static data, but use a stable
                value-based key instead of array index so the linter is happy
                and state-tracking is robust if the dataset ever grows. */}
            <div style={{ marginTop: 36, display: "flex", alignItems: "flex-end", gap: 6, height: 56 }}>
              {["jul","aug","sep","okt","nov","des","jan","feb","mar","apr","mai","jun"].map((m, i) => {
                const heights = [28, 42, 36, 58, 44, 70, 62, 80, 72, 88, 96, 100];
                const h = heights[i];
                return (
                  <div key={m} style={{ flex: 1, height: `${h}%`, background: i >= 10 ? P.champagne : `${P.champagne}55`, opacity: 0.8 }} />
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: M, fontSize: 9, color: P.inkDim, letterSpacing: "0.15em", marginTop: 6 }}>
              <span>JUL</span><span>AUG</span><span>SEP</span><span>OKT</span><span>NOV</span><span>DES</span><span>JAN</span><span>FEB</span><span>MAR</span><span>APR</span><span>MAI</span><span style={{ color: P.champagne }}>JUN</span>
            </div>
          </div>
        </div>

        {/* Right: flight conversion */}
        <div style={{ padding: "32px 36px", background: P.bg }}>
          <div style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.3em", color: P.inkDim, marginBottom: 18 }}>NESTE DESTINASJON</div>
          <div style={{ fontFamily: D, fontSize: 36, fontWeight: 500, letterSpacing: "-0.02em", lineHeight: 1.05 }}>
            <em style={{ fontStyle: "italic", color: P.champagne }}>København</em><br />— en helgetur unna.
          </div>
          <div style={{ marginTop: 18, padding: 18, border: `1px solid ${P.divider}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: M, fontSize: 10, letterSpacing: "0.15em", color: P.inkDim }}>
              <span>OSL → CPH · ÉN VEI</span>
              <span style={{ color: P.champagne }}>10 000 P</span>
            </div>
            <div style={{ marginTop: 12, height: 6, background: P.divider, position: "relative" }}>
              <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: "100%", background: P.champagne }} />
              <div style={{ position: "absolute", right: -4, top: -3, width: 12, height: 12, borderRadius: "50%", background: P.champagne, border: `2px solid ${P.bg}` }} />
            </div>
            <div style={{ marginTop: 10, fontFamily: M, fontSize: 10, letterSpacing: "0.15em", color: "#76C893" }}>★ DU HAR NOK POENG</div>
          </div>
          <div style={{ marginTop: 14, padding: 18, border: `1px solid ${P.divider}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontFamily: M, fontSize: 10, letterSpacing: "0.15em", color: P.inkDim }}>
              <span>OSL → JFK · ÉN VEI</span>
              <span>40 000 P</span>
            </div>
            <div style={{ marginTop: 12, height: 6, background: P.divider, position: "relative" }}>
              <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: "31%", background: P.champagneSoft }} />
            </div>
            <div style={{ marginTop: 10, fontFamily: M, fontSize: 10, letterSpacing: "0.15em", color: P.inkDim }}>27 520 POENG IGJEN</div>
          </div>
        </div>
      </div>

      {/* Recent flights (merchants) */}
      <div style={{ padding: "32px 36px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 18 }}>
          <div style={{ fontFamily: D, fontSize: 30, fontWeight: 500, letterSpacing: "-0.02em" }}>Siste flights</div>
          <span style={{ fontFamily: M, fontSize: 10, letterSpacing: "0.2em", color: P.inkDim }}>SE ALLE 24 →</span>
        </div>
        <div style={{ border: `1px solid ${P.divider}` }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1.2fr 0.8fr 80px", padding: "10px 20px", background: P.bg, fontFamily: M, fontSize: 9, letterSpacing: "0.25em", color: P.inkDim, borderBottom: `1px solid ${P.divider}` }}>
            <span>FLIGHT</span><span>FORHANDLER</span><span>EARN</span><span>METODE</span><span style={{ textAlign: "right" }}>DATO</span>
          </div>
          {[
            ["SK 4302", "Komplett", "komplett.no", "+225 EuroBonus", "Visa Gull", "14.06"],
            ["SK 1140", "Meny", "meny.no", "+3% Trumf · 84 kr", "Vipps", "12.06"],
            ["SK 8801", "XXL Sport", "xxl.no", "+120 EuroBonus", "Mastercard", "09.06"],
            ["SK 6604", "LEGO", "lego.com", "+45 EuroBonus", "Apple Pay", "06.06"],
            ["SK 9120", "Hotels.com", "hotels.com", "+820 EuroBonus", "Amex Plat.", "02.06"],
          ].map(([f, n, h, e, m, d]) => (
            <div key={f} style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 1.2fr 0.8fr 80px", padding: "14px 20px", borderTop: `1px solid ${P.divider}`, alignItems: "center", fontSize: 13 }}>
              <span style={{ fontFamily: M, fontSize: 11, color: P.champagne, letterSpacing: "0.12em" }}>{f}</span>
              <div>
                <div style={{ fontFamily: D, fontSize: 17, fontWeight: 500 }}>{n}</div>
                <div style={{ fontFamily: M, fontSize: 10, color: P.inkDim, letterSpacing: "0.08em" }}>{h}</div>
              </div>
              <span style={{ fontFamily: M, fontSize: 11, color: P.champagne, letterSpacing: "0.08em" }}>{e}</span>
              <span style={{ fontFamily: M, fontSize: 11, color: P.inkDim, letterSpacing: "0.08em" }}>{m}</span>
              <span style={{ fontFamily: M, fontSize: 11, color: P.inkDim, letterSpacing: "0.1em", textAlign: "right" }}>{d}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
